import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Intercepts the browser back button and asks the user to confirm before
 * navigating away. Works by pushing a sentinel history entry so the back
 * press triggers a popstate event instead of leaving the page.
 */
export function useBackGuard(isReady: boolean) {
    const [showBackWarning, setShowBackWarning] = useState(false);
    const popStateHandlerRef = useRef<(() => void) | null>(null);

    // Attaches a one-shot popstate listener. The handler removes itself before
    // showing the dialog so rapid back-presses can't bypass the confirmation.
    const armPopState = useCallback(() => {
        const handler = () => {
            window.removeEventListener('popstate', handler);
            popStateHandlerRef.current = null;
            setShowBackWarning(true);
        };
        popStateHandlerRef.current = handler;
        window.addEventListener('popstate', handler);
    }, []);

    useEffect(() => {
        if (!isReady) return;
        // Guard against React StrictMode's double-invocation: cleanup doesn't pop
        // pushState, so skip the push if the current entry is already our sentinel.
        if (window.history.state?.sentinel !== true) {
            window.history.pushState({ sentinel: true }, '');
        }
        armPopState();
        return () => {
            if (popStateHandlerRef.current) {
                window.removeEventListener('popstate', popStateHandlerRef.current);
                popStateHandlerRef.current = null;
            }
        };
    }, [isReady, armPopState]);

    const handleGoBack = () => {
        setShowBackWarning(false);
        window.history.back();
    };

    // User chose to stay: re-push the sentinel and re-arm the listener.
    const handleStay = () => {
        setShowBackWarning(false);
        window.history.pushState({ sentinel: true }, '');
        armPopState();
    };

    return { showBackWarning, handleGoBack, handleStay };
}
